import React, { useEffect } from 'react';
import {
  Box,
  Paper,
  Alert,
} from '@mui/material';
import {
  Login as LoginIcon,
  AccountCircle as AccountIcon,
  Person as PersonIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useLoginForm } from './hooks/useLoginForm';
import { useAuth } from '../../auth/hooks/useAuth';
import { AUTH_CONSTANTS } from '../../constants/auth';
import { type LoginPageProps } from '../../types/auth';
import { TextFieldComponent } from '../common/TextFieldComponent';
import { CheckboxComponent } from '../common/CheckboxComponent';
import { ButtonComponent } from '../common/ButtonComponent';
import { LinkComponent } from '../common/LinkComponent';
import { Title, Subtitle } from '../common/TypographyComponents';
import { useNavigate } from 'react-router-dom';
/**
 * LoginPage Component
 * 
 * Complete login page with form and validation
 * Follows clean architecture - delegates logic to hooks and services
 */
export const LoginPage: React.FC<LoginPageProps> = ({
  onLoginSuccess,
}) => {
  const { login, isLoading, error, clearError, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Auto-redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      console.log('User is authenticated in LoginPage, navigating to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const {
    formData,
    errors,
    isValid,
    isSubmitting,
    handleFieldChange,
    handleSubmit,
    clearErrors,
  } = useLoginForm({
    onSubmit: async (credentials) => {
      clearError();
      await login(credentials);
      onLoginSuccess?.();
    },
  });

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    await handleSubmit();
  };


  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 2,
        width: '100%',
      }}
    >
      {/* Main Login Card */}
      <Paper
          elevation={12}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 400,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            mx: 'auto',
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <AccountIcon
              sx={{
                fontSize: 48,
                color: 'primary.main',
                mb: 2,
              }}
            />
            <Title
              variant="h4"
              color="textPrimary"
              sx={{ mb: 1 }}
            >
              {AUTH_CONSTANTS.MESSAGES.welcome}
            </Title>
            <Subtitle
              variant="body2"
              color="textSecondary"
            >
              {AUTH_CONSTANTS.MESSAGES.loginSubtitle}
            </Subtitle>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              onClose={clearError}
              sx={{ mb: 3 }}
            >
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <Box component="form" onSubmit={handleFormSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Username Field */}
              <TextFieldComponent
                label={AUTH_CONSTANTS.FIELD_LABELS.username || 'Username'}
                type="text"
                value={formData.username}
                onChange={(value) => handleFieldChange('username', value)}
                error={errors.username}
                disabled={isLoading || isSubmitting}
                autoComplete="username"
                startIcon={<PersonIcon color={errors.username ? 'error' : 'action'} />}
              />

              {/* Password Field */}
              <TextFieldComponent
                label={AUTH_CONSTANTS.FIELD_LABELS.password}
                type="password"
                value={formData.password}
                onChange={(value) => handleFieldChange('password', value)}
                error={errors.password}
                disabled={isLoading || isSubmitting}
                autoComplete="current-password"
                startIcon={<LockIcon color={errors.password ? 'error' : 'action'} />}
                showPasswordToggle={true}
              />

              {/* Remember Me Checkbox */}
              <CheckboxComponent
                label={AUTH_CONSTANTS.FIELD_LABELS.rememberMe}
                checked={formData.rememberMe}
                onChange={(checked) => handleFieldChange('rememberMe', checked)}
                disabled={isLoading || isSubmitting}
                color="primary"
              />
            </Box>

            {/* Submit Button */}
            <ButtonComponent
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={!isValid}
              loading={isLoading || isSubmitting}
              startIcon={<LoginIcon />}
              loadingText={AUTH_CONSTANTS.BUTTON_LABELS.loading}
              sx={{
                mt: 3,
                mb: 2,
              }}
            >
              {AUTH_CONSTANTS.BUTTON_LABELS.login}
            </ButtonComponent>

            {/* Forgot Password Link */}
            <Box sx={{ textAlign: 'center' }}>
              <LinkComponent
                href="#"
                variant="body2"
                color="primary"
                underline="hover"
              >
                {AUTH_CONSTANTS.MESSAGES.forgotPassword}
              </LinkComponent>
            </Box>
          </Box>
      </Paper>
    </Box>
  );
};